import { Box, Button, HStack, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';
import { Receivable, Util } from '@finley-technologies/finley-types';

import { EditAssignmentDropdown } from '../../EditAssignmentDropdown';
import { BorrowingBaseFromReceivables } from './BorrowingBaseFromReceivables';
import { ConcentrationStats } from './ConcentrationStats';
import { EligibilityStats } from './EligibilityStats';
import { groupMultiLpConcentrationKeysByLoanProducts } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { CreditFacilitySelect } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useLatestAssignmentPipelineRunMetadataForLpGroupingContext } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForLpGroupingProvider';
import { useLoanProductCreditFacilityGroupContext } from 'packages/loanProduct/hooks/LoanProductCreditFacilityGroupingProvider';
import { usePreviewGenerationStatus } from 'packages/loanProduct/hooks/useGetPreviewGenerationStatus';
import { useGetAssignmentSummaryQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import { AssignmentSummaryConfirmButton } from 'packages/loanProduct/ui/AssignmentSummaryConfirmButton';
import { assignmentPagePaddingSide } from 'packages/loanProduct/ui/utils';
import {
  createLeftOnBalanceSheetStatsProps,
  LeftOnBalanceSheetStats,
} from 'packages/loanProduct/ui/v2/AssignmentSummary/LeftOnBalanceSheetStats';
import { createPortfolioTestStats } from 'packages/loanProduct/ui/v2/AssignmentSummary/TestStats';
import { AssignmentSummaryTopLevelStats } from 'packages/loanProduct/ui/v2/AssignmentSummary/TopLevelStats';
import { parseAssignmentSummaryPreview } from 'packages/loanProduct/ui/v2/AssignmentSummary/utils';
import { AssignmentTemplate } from 'packages/loanProduct/ui/v2/AssignmentTemplate';
import { CheckLatestPipelineRunStatus } from 'packages/loanProduct/ui/v2/CheckLatestPipelineRunStatus';
import { DownloadPreviewButton } from 'packages/loanProduct/ui/v2/DownloadPreviewButton';
import { EmailServicerReportPreviewButton } from 'packages/loanProduct/ui/v2/EmailServicerReportPreviewButton';

const AssignmentSummaryLoadingTemplate = () => {
  return (
    <AssignmentTemplate
      selectedTab="overview"
      headerChildren={
        <HStack spacing="32px">
          <EditAssignmentDropdown isDisabled />
          <Button variant="solid" colorScheme="aurora" isDisabled>
            Confirm Assignments
          </Button>
        </HStack>
      }
    >
      <Skeleton datatestid="loading-assignment-summary-body" height="80vh" />
    </AssignmentTemplate>
  );
};

export const AssignmentSummaryBody = ({
  masterLoanTapeIds,
  organizationId,
  previewIdentifier,
  reportingDate,
  fileVersion,
  flags,
}: {
  masterLoanTapeIds: string[];
  organizationId: string;
  previewIdentifier: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  flags?: LDFlagSet;
}) => {
  const { creditFacilityId } = useCreditFacilityContext();
  const toast = useToast();
  const history = useHistory();

  const {
    data: assignmentSummariesData,
    loading: loadingAssignmentSummaries,
  } = useGetAssignmentSummaryQuery({
    fetchPolicy: 'no-cache',
    variables: {
      previewIdentifier,
      creditFacilityId,
      reportingDate,
      fileVersion,
    },
  });

  const {
    data: creditAgreementData,
    loading: creditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    fetchPolicy: 'network-only',
  });

  if (
    !assignmentSummariesData ||
    !assignmentSummariesData.initial ||
    !assignmentSummariesData.proposed ||
    loadingAssignmentSummaries ||
    creditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact
  ) {
    return <AssignmentSummaryLoadingTemplate />;
  }

  const {
    currentCreditAgreementFullArtifact: {
      creditAgreement,
      id: creditAgreementId,
    },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
  } catch (err) {
    Sentry.captureException(err);

    toast({
      ...errorToastConfig,
      id: 'errorGetCurrentCreditAgreementFullArtifact',
      title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
    });
    history.push('/');
  }

  finleyAssert(
    parsedCreditAgreement,
    `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );

  const initialSummaryPreview = parseAssignmentSummaryPreview(
    assignmentSummariesData.initial,
    true,
  );

  const proposedSummaryPreview = parseAssignmentSummaryPreview(
    assignmentSummariesData.proposed,
    false,
  );

  const { data: leftOnBalanceSheetData } = createLeftOnBalanceSheetStatsProps({
    initial: initialSummaryPreview.borrowingBaseReport,
    proposed: proposedSummaryPreview.borrowingBaseReport,
  });

  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: { multiLoanProductConcentrationLimits },
    },
  } = parsedCreditAgreement;

  const multiLpConcentrationKeysByLoanProductsGrouping = groupMultiLpConcentrationKeysByLoanProducts(
    { multiLoanProductConcentrationLimits },
  );

  const initialPortfolioTestStats = createPortfolioTestStats({
    summary: initialSummaryPreview.borrowingBaseReport,
    creditAgreement: parsedCreditAgreement,
    multiLpConcentrationKeysByLoanProductsGrouping,
  });

  const proposedPortfolioTestStats = createPortfolioTestStats({
    summary: proposedSummaryPreview.borrowingBaseReport,
    creditAgreement: parsedCreditAgreement,
    multiLpConcentrationKeysByLoanProductsGrouping,
  });

  return (
    <Box height="100%">
      <AssignmentTemplate
        bg={colors.millet[50]}
        selectedTab="overview"
        headerChildren={
          <HStack spacing="32px">
            <EditAssignmentDropdown flags={flags} />
            <AssignmentSummaryConfirmButton
              masterLoanTapeIds={masterLoanTapeIds}
              previewIdentifier={proposedSummaryPreview.previewIdentifier}
              reportingDate={reportingDate}
              fileVersion={fileVersion}
            />
          </HStack>
        }
      >
        <HStack
          m="32px 0"
          w="100%"
          pt="32px"
          alignItems="end"
          justifyContent="space-between"
        >
          <CreditFacilitySelect
            displayInputFloatingLabel
            inputFloatingLabelBackgroundColor={colors.millet[50]}
            organizationId={organizationId}
            width="100%"
          />
          <HStack spacing="16px">
            <EmailServicerReportPreviewButton
              creditFacilityId={creditFacilityId}
              fileVersion={fileVersion}
              reportingDate={reportingDate}
              fileNamePrefix="extended-receivable-response-tape-preview"
              previewIdentifier={proposedSummaryPreview.previewIdentifier}
            />
            <DownloadPreviewButton
              creditFacilityId={creditFacilityId}
              reportingDate={reportingDate}
              fileVersion={fileVersion}
              previewIdentifier={proposedSummaryPreview.previewIdentifier}
            />
          </HStack>
        </HStack>
        <AssignmentSummaryTopLevelStats
          initial={initialSummaryPreview.borrowingBaseReport}
          proposed={proposedSummaryPreview.borrowingBaseReport}
          reportingDate={reportingDate}
          creditAgreement={parsedCreditAgreement}
        />
        <EligibilityStats
          initialStats={initialPortfolioTestStats}
          proposedStats={proposedPortfolioTestStats}
          creditAgreement={parsedCreditAgreement}
        />
        <ConcentrationStats
          initialStats={initialPortfolioTestStats}
          proposedStats={proposedPortfolioTestStats}
          creditAgreement={parsedCreditAgreement}
          multiLpConcentrationKeysByLoanProductsGrouping={
            multiLpConcentrationKeysByLoanProductsGrouping
          }
        />
        <BorrowingBaseFromReceivables
          initial={initialSummaryPreview.borrowingBaseReport}
          proposed={proposedSummaryPreview.borrowingBaseReport}
          creditAgreement={parsedCreditAgreement}
        />
      </AssignmentTemplate>
      <Box bg="white" p={`32px ${assignmentPagePaddingSide}`}>
        <LeftOnBalanceSheetStats data={leftOnBalanceSheetData} />
      </Box>
    </Box>
  );
};

export const AssignmentSummary = withLDConsumer()(
  ({
    organizationId,
    flags,
  }: {
    organizationId: string;
    flags?: LDFlagSet;
  }) => {
    const { lpToCfEntityGrouping } = useLoanProductCreditFacilityGroupContext();
    const {
      metadata,
    } = useLatestAssignmentPipelineRunMetadataForLpGroupingContext();
    const {
      isCompleted,
      latestJob,
      latestPreviewIdentifier,
      StatusAlert: PreviewStatusAlert,
    } = usePreviewGenerationStatus({
      loanProductIds:
        lpToCfEntityGrouping?.loanProducts.map(({ id }) => id) ?? [],
      organizationId,
      reportingDate: metadata?.reportingDate,
      fileVersion: metadata?.fileVersion,
    });

    return (
      <Box height="100%" w="100%">
        <CheckLatestPipelineRunStatus>
          {({ StatusAlert }) => {
            if (metadata === undefined || isCompleted === null) {
              return <AssignmentSummaryLoadingTemplate />;
            }
            if (
              !metadata ||
              metadata.status !== 'success' ||
              (metadata.status === 'success' && !isCompleted)
            ) {
              let status: Receivable.Assignment.Pipeline.Status;
              if (!metadata) {
                status = 'noPipelineRun';
              } else if (!isCompleted) {
                status = 'inProgress';
              } else {
                status = metadata.status;
              }

              return (
                <AssignmentTemplate
                  selectedTab="overview"
                  headerChildren={
                    <HStack spacing="32px">
                      <EditAssignmentDropdown flags={flags} />
                      <Button variant="solid" colorScheme="aurora" isDisabled>
                        Confirm Assignments
                      </Button>
                    </HStack>
                  }
                >
                  <StatusAlert status={status} />
                </AssignmentTemplate>
              );
            }
            if (latestJob === null) {
              return <AssignmentSummaryLoadingTemplate />;
            }

            return latestJob.status === 'completed' &&
              latestPreviewIdentifier ? (
              <AssignmentSummaryBody
                previewIdentifier={latestPreviewIdentifier}
                masterLoanTapeIds={metadata.masterLoanTapeIds}
                fileVersion={metadata.fileVersion}
                reportingDate={metadata.reportingDate}
                organizationId={organizationId}
                flags={flags}
              />
            ) : (
              <AssignmentTemplate
                selectedTab="overview"
                headerChildren={
                  <HStack spacing="32px">
                    <EditAssignmentDropdown flags={flags} />
                    <Button variant="solid" colorScheme="aurora" isDisabled>
                      Confirm Assignments
                    </Button>
                  </HStack>
                }
              >
                <PreviewStatusAlert
                  status={latestPreviewIdentifier ? latestJob.status : 'error'}
                  text={
                    latestJob?.assignmentMethod === 'csv'
                      ? 'An error occurred during the latest assignment process. Review your .CSV file(s) and try again.'
                      : undefined
                  }
                />
              </AssignmentTemplate>
            );
          }}
        </CheckLatestPipelineRunStatus>
      </Box>
    );
  },
);
